/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { useLanguage } from "../../contexts/LanguageContext";
import { buildPurchaseEvent, openSupportChat, pushPurchaseOnce, track, trackOnce } from "../../utils/analytics";
import { chromeIntentUrl, getBrowserContext } from "../../utils/browser";
import { safeStorage } from "../../utils/storage";
import { PAPER_SIZES } from "../../constants/flyer";
import { CHECKOUT_RETURNED_KEY, returnedFromCheckout } from "./checkout";
import { useStartData } from "./StartContext";
import { useFlyerJob } from "./FlyerJob";
import FlyerRender from "./FlyerRender";
import { flyerDescriptionHtml } from "./flyerBuilder";
import { ensureFlyerRecord } from "./setupChain";
import * as startApi from "./api";
import {
  Chip,
  ErrorBox,
  Field,
  InfoBox,
  PrimaryButton,
  SecondaryButton,
  Section,
  Spinner,
  StickyFooter,
  TextInput,
  cx,
} from "./ui";

// Done: where Stripe sends people back to, and the page that stays "your
// program is live" afterwards (/setup sends people from this flow here too).
//
// Stripe's webhook, not the redirect, is what tells the backend about the
// trial, and it can land after the person is back. So the page keeps asking
// check_setup until the store reads as set up, and only then lets them into
// the dashboard: the dashboard's guards would otherwise send them straight
// back into onboarding. Nothing here offers a second checkout while waiting.

const POLL_MS = 3000;
// After this the page says activation is slow and lets the person leave…
const SLOW_AFTER_MS = 60000;
// …but keeps checking now and then, so a late webhook still flips it to live.
const SLOW_POLL_MS = 15000;
const GIVE_UP_AFTER_MS = 10 * 60000;

const PAPER_ORDER = ["a4", "letter", "a5", "a6"];

// Remembers that the flyer was re-drawn without the "Powered by" footer after
// an Advanced checkout (the first PDF was drawn before the plan was known).
const PLAN_RENDER_KEY = "start:flyerPlanRender";

const noop = () => {};

function nextDelay(elapsed) {
  if (elapsed >= GIVE_UP_AFTER_MS) return null;
  return elapsed >= SLOW_AFTER_MS ? SLOW_POLL_MS : POLL_MS;
}

// In-app browsers often refuse the async clipboard; execCommand still works
// in most of them, and the caller shows a selectable field when neither does.
async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the old way
  }
  let area = null;
  try {
    area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    // 16px so iOS does not zoom, off screen so nothing flashes.
    area.style.cssText = "position:fixed;top:0;left:-9999px;font-size:16px;opacity:0";
    document.body.appendChild(area);
    area.select();
    area.setSelectionRange(0, text.length);
    return document.execCommand("copy") === true;
  } catch {
    return false;
  } finally {
    area?.remove();
  }
}

function canShareFiles(files) {
  try {
    return (
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function" &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files }) === true
    );
  } catch {
    return false;
  }
}

// Whether this browser can share a PDF at all, asked before any flyer exists so
// the page knows whether to draw one locally.
function detectFileShare() {
  try {
    return canShareFiles([new File(["%PDF-1.4"], "flyer.pdf", { type: "application/pdf" })]);
  } catch {
    return false;
  }
}

// The largest set the share sheet accepts: PDF and image, else either one.
function pickShareFiles(pdfFile, jpegFile) {
  const candidates = [[pdfFile, jpegFile].filter(Boolean), [jpegFile].filter(Boolean), [pdfFile]];
  return candidates.find((files) => files.length > 0 && canShareFiles(files)) || null;
}

function flyerFileName(paperSize, ext) {
  return `loyaltify-flyer-${paperSize}.${ext}`;
}

function CheckIcon({ className = "h-4 w-4" }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className={className} fill="currentColor">
      <path
        fillRule="evenodd"
        d="M16.7 5.3a1 1 0 0 1 0 1.4l-8 8a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.4L8 12.58l7.3-7.3a1 1 0 0 1 1.4 0Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className="h-5 w-5 shrink-0 text-gray-400" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M7.2 14.8a1 1 0 0 1 0-1.4L10.58 10 7.2 6.6a1 1 0 0 1 1.4-1.4l4.1 4.1a1 1 0 0 1 0 1.4l-4.1 4.1a1 1 0 0 1-1.4 0Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

const tintActionClass = cx(
  "inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#EEEBFE] px-5 text-base font-semibold text-[#2F12B8] transition hover:bg-[#DDD9FC]",
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4522ED]",
);

const outlineActionClass = cx(
  "inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-5 text-base font-semibold text-gray-900 transition hover:bg-gray-50",
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4522ED]",
);

// Copy with the in-app browser fallbacks: a selectable field when copying is
// refused, so the person can still long-press it.
function CopyAction({ text, label, copiedLabel, manualLabel, manualHint, tip, onCopied }) {
  const inputId = useId();
  const [state, setState] = useState("idle");

  const handleCopy = async () => {
    const copied = await copyText(text);
    setState(copied ? "copied" : "manual");
    if (copied) onCopied?.();
  };

  return (
    <div>
      <SecondaryButton onClick={handleCopy}>
        {state === "copied" && <CheckIcon className="h-5 w-5 text-green-600" />}
        {state === "copied" ? copiedLabel : label}
      </SecondaryButton>
      <p className="sr-only" aria-live="polite">
        {state === "copied" ? copiedLabel : ""}
      </p>
      {tip && <p className="mt-2 text-sm text-gray-500">{tip}</p>}
      {state === "manual" && (
        <div className="mt-3">
          <Field label={manualLabel} hint={manualHint} htmlFor={inputId}>
            <TextInput
              id={inputId}
              readOnly
              value={text}
              autoFocus
              onFocus={(event) => event.target.select()}
              className="font-mono text-sm"
            />
          </Field>
        </div>
      )}
    </div>
  );
}

function Checklist({ items }) {
  const { t } = useLanguage();
  return (
    <ul className="grid grid-cols-2 gap-2" aria-label={t("start.done.checklist.label")}>
      {items.map((item) => (
        <li
          key={item.id}
          className="flex min-h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900"
        >
          {item.done ? (
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700">
              <CheckIcon />
            </span>
          ) : (
            <Spinner className="h-6 w-6 shrink-0 text-gray-400" />
          )}
          <span className="min-w-0">{item.label}</span>
          <span className="sr-only">
            {item.done ? t("start.done.checklist.ready") : t("start.done.checklist.pending")}
          </span>
        </li>
      ))}
    </ul>
  );
}

function NextStep({ to, title, body, onClick }) {
  return (
    <li>
      <Link
        to={to}
        onClick={onClick}
        className="flex min-h-[56px] items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 transition hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4522ED]"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold text-gray-900">{title}</span>
          {body && <span className="block text-sm text-gray-500">{body}</span>}
        </span>
        <ChevronIcon />
      </Link>
    </li>
  );
}

export default function Done() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { store, storeId, status, startPass, initForm, flyer, refresh } = useStartData();
  const flyerJob = useFlyerJob();
  const { ensurePdf, renderFiles, colors } = flyerJob;

  const browser = useMemo(() => getBrowserContext(), []);
  const inApp = browser.isInAppBrowser;
  const shareCapable = useMemo(() => detectFileShare(), []);
  // Only these need the flyer as files on the phone; the in-app browsers
  // without a share sheet get links to the stored PDF instead.
  const wantLocalFiles = shareCapable || !inApp;

  // Decided once, on arrival: the purchase and trial events belong to the
  // return from Stripe, not to every later visit of this page.
  const [returned] = useState(() => returnedFromCheckout(location.search));

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // ---- Return from Stripe -------------------------------------------------

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("payment") !== "success") return;
    // Recorded before the URL loses it: the step guard reads the marker to
    // keep this page (and not the plan picker) while the webhook is pending.
    safeStorage.set(CHECKOUT_RETURNED_KEY, Date.now());
    navigate(location.pathname, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    track("start_done_viewed", { activated: !!status?.setup_completed, returned });
    if (returned && storeId) trackOnce(`start_trial_started:${storeId}`, "start_trial_started");
    // Once per visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Settles the purchase from a fresh store read, outside the poll below and
  // not tied to this page staying mounted: called the moment the store goes
  // live and when the person leaves, so a quick exit cannot lose the event.
  const settlePurchaseNow = useCallback(() => {
    if (!returned || !storeId) return;
    startApi
      .fetchStoreSilently(storeId)
      .then((fresh) => pushPurchaseOnce(fresh))
      .catch(() => {});
  }, [returned, storeId]);

  // ---- Activation -----------------------------------------------------------

  const [polledLive, setPolledLive] = useState(false);
  const [slow, setSlow] = useState(false);
  const live = polledLive || !!status?.setup_completed;
  const liveResult = useRef(null);

  // Puts "set up" into the cache the dashboard's guards read, so they let the
  // person in instead of sending them back to onboarding. A check_setup fetch
  // still in flight could land afterwards with the older answer, so it is
  // cancelled first.
  const markLive = useCallback(
    async (result) => {
      if (!storeId) return;
      const key = ["setup-status", storeId];
      liveResult.current = { ...(result || queryClient.getQueryData(key) || {}), setup_completed: true };
      try {
        await queryClient.cancelQueries({ queryKey: key });
      } catch {
        // nothing in flight
      }
      queryClient.setQueryData(key, liveResult.current);
      queryClient.invalidateQueries({ queryKey: ["stores"] });
    },
    [queryClient, storeId],
  );

  // The poll reads these through refs so a language change or a refreshed
  // record does not restart its clock.
  const latest = useRef({});
  latest.current = { flyer, initForm, colors, t, refresh };
  const flyerEnsured = useRef(false);
  // Shared by overlapping rounds, so a slow create is never sent twice.
  const flyerEnsuring = useRef(null);

  useEffect(() => {
    if (!storeId || live) return undefined;
    let cancelled = false;
    let timer = null;
    const started = Date.now();

    const tick = async () => {
      const { flyer: currentFlyer, initForm: form, colors: flyerColors, t: translate } = latest.current;
      // check_setup only reports set up once a flyer exists; an earlier step
      // may have been interrupted before creating it.
      if (!currentFlyer && !flyerEnsured.current && form?.id) {
        flyerEnsuring.current =
          flyerEnsuring.current ||
          ensureFlyerRecord({ storeId, formId: form.id, paperSize: "a4", colors: flyerColors, t: translate })
            .then(() => {
              flyerEnsured.current = true;
              latest.current.refresh?.();
            })
            .finally(() => {
              flyerEnsuring.current = null;
            });
        try {
          await flyerEnsuring.current;
        } catch {
          // tried again on the next round
        }
        if (cancelled) return;
      }
      try {
        const result = await startApi.checkSetup(storeId);
        if (result?.setup_completed) {
          setPolledLive(true);
          settlePurchaseNow();
          await markLive(result);
          return;
        }
      } catch {
        // a failed check is just another "not yet"
      }
      if (cancelled) return;
      const elapsed = Date.now() - started;
      if (elapsed >= SLOW_AFTER_MS) setSlow(true);
      const delay = nextDelay(elapsed);
      if (delay != null) timer = setTimeout(tick, delay);
    };

    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [storeId, live, markLive, settlePurchaseNow]);

  // ---- Purchase event -------------------------------------------------------

  // Any refetched store (e.g. after the stores list is invalidated) can carry
  // the plan the webhook filled in.
  useEffect(() => {
    if (returned) pushPurchaseOnce(store);
  }, [returned, store, store?.stripe_subscription_id, store?.current_plan, store?.billing_cycle]);

  // The same 'purchase' push as /setup, once the webhook has filled in the
  // plan. Only for a return from Stripe, so a later visit on another device
  // does not count the sale again.
  const storeRef = useRef(store);
  storeRef.current = store;
  useEffect(() => {
    if (!returned || !storeId) return undefined;
    let cancelled = false;
    let timer = null;
    const started = Date.now();

    const settle = (candidate) => {
      if (!buildPurchaseEvent(candidate)) return false;
      pushPurchaseOnce(candidate);
      return true;
    };

    const tick = async () => {
      try {
        const fresh = await startApi.fetchStoreSilently(storeId);
        if (cancelled) return;
        if (settle(fresh)) {
          // The plan also decides the flyer footer; let the page see it.
          queryClient.invalidateQueries({ queryKey: ["stores"] });
          return;
        }
      } catch {
        // keep trying
      }
      if (cancelled) return;
      const delay = nextDelay(Date.now() - started);
      if (delay != null) timer = setTimeout(tick, delay);
    };

    if (!settle(storeRef.current)) tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [returned, storeId, queryClient]);

  // ---- Flyer ------------------------------------------------------------

  const [paperSize, setPaperSize] = useState(() =>
    PAPER_SIZES[flyer?.paper_size] ? flyer.paper_size : "a4",
  );
  const [paperError, setPaperError] = useState(false);
  const [localFiles, setLocalFiles] = useState(null);
  const [localFailedFor, setLocalFailedFor] = useState(null);
  const [localBusy, setLocalBusy] = useState(false);
  const [shareFailed, setShareFailed] = useState(false);
  // FlyerJob draws one flyer at a time, and a second render started while one
  // runs would replace it and leave the first never finishing. Every render
  // this screen starts goes through this flag.
  const jobBusy = useRef(false);
  const changingSize = useRef(false);

  const jobWorking = flyerJob.status === "working";
  const jobFiles = flyerJob.files?.paperSize === paperSize ? flyerJob.files : null;
  // FlyerJob only keeps files it has uploaded, so those count as on the server
  // even before the refreshed flyer record says so.
  const pdfOnServer = !!flyer?.pdf_url || !!flyerJob.files;

  useEffect(() => {
    if (changingSize.current || !PAPER_SIZES[flyer?.paper_size]) return;
    setPaperSize(flyer.paper_size);
  }, [flyer?.paper_size]);

  const runJob = useCallback(async (job) => {
    jobBusy.current = true;
    try {
      return await job();
    } finally {
      jobBusy.current = false;
    }
  }, []);

  // A flyer made without a PDF (or whose PDF upload was cut off) gets one now.
  const pdfRequested = useRef(false);
  useEffect(() => {
    if (!flyer || flyer.pdf_url || pdfRequested.current || jobBusy.current) return;
    pdfRequested.current = true;
    runJob(() => ensurePdf()).catch(noop);
  }, [flyer, ensurePdf, runJob]);

  const retryPdf = () => {
    if (jobBusy.current) return;
    runJob(() => ensurePdf({ paperSize })).catch(noop);
  };

  // The first PDF may have been drawn before the plan was known, with the
  // "Powered by" footer an Advanced plan leaves out. Re-drawn once.
  useEffect(() => {
    if (!returned || store?.current_plan !== "advanced" || !flyer?.id || !flyer.pdf_url) return;
    if (jobWorking || jobBusy.current || safeStorage.get(`${PLAN_RENDER_KEY}:${flyer.id}`)) return;
    safeStorage.set(`${PLAN_RENDER_KEY}:${flyer.id}`, true);
    runJob(() => ensurePdf({ force: true, paperSize: flyer.paper_size })).catch(noop);
  }, [returned, store?.current_plan, flyer, jobWorking, ensurePdf, runJob]);

  // Files for the share sheet and the download link are made before the tap:
  // anything async between the tap and navigator.share loses the user
  // activation, and the share is refused.
  const blobs = jobFiles || (localFiles?.paperSize === paperSize ? localFiles : null);
  const localFailed = localFailedFor === paperSize;

  useEffect(() => {
    if (!wantLocalFiles || !pdfOnServer || blobs || localFailed) return;
    if (jobWorking || jobBusy.current) return;
    const size = paperSize;
    setLocalBusy(true);
    runJob(() => renderFiles({ paperSize: size }))
      .then((files) => {
        if (mounted.current) setLocalFiles({ ...files, paperSize: size });
      })
      .catch(() => {
        if (mounted.current) setLocalFailedFor(size);
      })
      .finally(() => {
        if (mounted.current) setLocalBusy(false);
      });
  }, [wantLocalFiles, pdfOnServer, blobs, localFailed, jobWorking, paperSize, renderFiles, runJob]);

  const shareFiles = useMemo(() => {
    if (!shareCapable || !blobs?.pdf) return null;
    try {
      const pdfFile = new File([blobs.pdf], flyerFileName(blobs.paperSize, "pdf"), { type: "application/pdf" });
      const jpegFile = blobs.jpeg
        ? new File([blobs.jpeg], flyerFileName(blobs.paperSize, "jpg"), { type: "image/jpeg" })
        : null;
      return pickShareFiles(pdfFile, jpegFile);
    } catch {
      return null;
    }
  }, [blobs, shareCapable]);

  const [pdfHref, setPdfHref] = useState(null);
  useEffect(() => {
    if (!blobs?.pdf || inApp) {
      setPdfHref(null);
      return undefined;
    }
    let url = null;
    try {
      url = URL.createObjectURL(blobs.pdf);
    } catch {
      url = null;
    }
    setPdfHref(url);
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [blobs, inApp]);

  const changePaperSize = async (size) => {
    if (size === paperSize || jobBusy.current || jobWorking || !flyer) return;
    const previous = paperSize;
    changingSize.current = true;
    setPaperSize(size);
    setPaperError(false);
    setShareFailed(false);
    track("start_paper_size_changed", { paperSize: size });
    try {
      await runJob(() => ensurePdf({ force: true, paperSize: size }));
    } catch {
      if (mounted.current) {
        setPaperSize(previous);
        setPaperError(true);
      }
    } finally {
      changingSize.current = false;
    }
  };

  const publicFlyer = flyer?.uuid ? startApi.publicFlyerUrl(flyer.uuid) : null;
  const showShare = !!shareFiles && !shareFailed;
  // The stored PDF stands in when the phone could not draw or hold its own.
  const downloadHref =
    !inApp && pdfOnServer ? pdfHref || ((localFailed || blobs) && publicFlyer) || null : null;
  const showChrome = inApp && browser.platform === "android" && pdfOnServer && !!publicFlyer;
  const showCopyFlyer = inApp && browser.platform !== "android" && pdfOnServer && !!publicFlyer && !showShare;
  const hasFlyerAction = showShare || !!downloadHref || showChrome || showCopyFlyer;
  const flyerBusy = jobWorking || localBusy || !flyer || (!pdfOnServer && flyerJob.status !== "error");
  const flyerFailed = !pdfOnServer && flyerJob.status === "error";

  // Synchronous on purpose: see the files note above.
  const handleShare = () => {
    if (!shareFiles) return;
    const name = store?.name?.trim();
    try {
      navigator
        .share({ files: shareFiles, ...(name ? { title: t("start.done.flyer.shareTitle", { name }) } : {}) })
        .then(() => track("start_flyer_shared", { paperSize }))
        .catch((error) => {
          if (error?.name !== "AbortError" && mounted.current) setShareFailed(true);
        });
    } catch {
      setShareFailed(true);
    }
  };

  // ---- Leaving -----------------------------------------------------------

  const goTo = async (event, path) => {
    event?.preventDefault?.();
    settlePurchaseNow();
    if (live) await markLive(liveResult.current || status);
    navigate(path);
  };

  const getHelp = () => {
    track("start_help_opened", { step: "done" });
    if (!openSupportChat(t("start.done.activating.helpMessage"))) {
      window.location.href = "mailto:support@loyaltify.io";
    }
  };

  // ---- Render -----------------------------------------------------------

  const formUrl = initForm?.uuid ? startApi.publicFormUrl(initForm.uuid) : null;
  const paper = PAPER_SIZES[paperSize] || PAPER_SIZES.a4;
  const descriptionHtml = useMemo(() => flyerDescriptionHtml(t), [t]);
  const storeName = store?.name?.trim();

  const checklist = [
    { id: "card", label: t("start.done.checklist.card"), done: !!startPass },
    { id: "form", label: t("start.done.checklist.form"), done: !!initForm },
    { id: "flyer", label: t("start.done.checklist.flyer"), done: !!flyer },
    { id: "trial", label: t("start.done.checklist.trial"), done: live || !!status?.has_subscription },
  ];

  return (
    <div className="space-y-5">
      <div>
        {live ? (
          <p className="inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-semibold text-green-800">
            <CheckIcon />
            {t("start.done.liveBadge")}
          </p>
        ) : (
          <p className="inline-flex items-center gap-1.5 rounded-full border border-[#DDD9FC] bg-[#F7F6FF] px-3 py-1 text-xs font-semibold text-[#2F12B8]">
            <Spinner className="h-4 w-4 text-[#4522ED]" />
            {t("start.done.activating.badge")}
          </p>
        )}
        <h1 className="mt-3 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          {live
            ? storeName
              ? t("start.done.title", { name: storeName })
              : t("start.done.titleNoName")
            : t("start.done.activating.title")}
        </h1>
        <p className="mt-1 text-base text-gray-600" role="status" aria-live="polite">
          {live ? t("start.done.subtitle") : t("start.done.activating.status")}
        </p>
      </div>

      {!live && slow && (
        <InfoBox tone="gray">
          <p className="font-semibold text-gray-900">{t("start.done.activating.slowTitle")}</p>
          <p className="mt-1">{t("start.done.activating.slowBody")}</p>
          <div className="mt-3">
            <SecondaryButton onClick={getHelp}>{t("start.done.activating.help")}</SecondaryButton>
          </div>
        </InfoBox>
      )}

      <Checklist items={checklist} />

      <Section title={t("start.done.flyer.title")} description={t("start.done.flyer.description")}>
        <div
          className="mx-auto w-full max-w-[260px] overflow-hidden rounded-lg shadow-md ring-1 ring-black/5"
          style={{ containerType: "inline-size", aspectRatio: `${paper.width} / ${paper.height}` }}
          role="img"
          aria-label={t("start.done.flyer.previewLabel")}
        >
          {formUrl && (
            <FlyerRender
              logoUrl={store?.logo_url || null}
              headerText={t("flyers.new.form.defaultHeaderText")}
              formUrl={formUrl}
              descriptionHtml={descriptionHtml}
              backgroundColor={colors?.backgroundColor}
              textColor={colors?.textColor}
              showFooter={store?.current_plan !== "advanced"}
              style={{ width: "100%", height: "100%" }}
            />
          )}
        </div>

        {flyer && (
          <div>
            <p id="start-done-paper" className="mb-2 text-sm font-semibold text-gray-800">
              {t("start.done.flyer.paperSize")}
            </p>
            <div role="group" aria-labelledby="start-done-paper" className="flex flex-wrap gap-2">
              {PAPER_ORDER.map((size) => (
                <Chip
                  key={size}
                  selected={paperSize === size}
                  disabled={(jobWorking || localBusy) && paperSize !== size}
                  onClick={() => changePaperSize(size)}
                  className="disabled:opacity-50"
                >
                  {PAPER_SIZES[size].name}
                </Chip>
              ))}
            </div>
            {paperError && (
              <p className="mt-2 text-sm font-medium text-red-700" role="alert">
                {t("start.done.flyer.paperError")}
              </p>
            )}
          </div>
        )}

        <div aria-live="polite">
          {flyerFailed ? (
            <ErrorBox onRetry={retryPdf} retryLabel={t("start.done.flyer.retry")}>
              {t("start.done.flyer.error")}
            </ErrorBox>
          ) : flyerBusy || !hasFlyerAction ? (
            <p className="flex min-h-12 items-center justify-center gap-2 rounded-xl bg-gray-50 px-4 text-sm font-medium text-gray-600">
              <Spinner className="h-5 w-5 text-[#4522ED]" />
              {t("start.done.flyer.preparing")}
            </p>
          ) : (
            <div className="space-y-2">
              {showShare && (
                <button type="button" onClick={handleShare} className={tintActionClass}>
                  {t("start.done.flyer.share")}
                </button>
              )}
              {downloadHref && (
                <a
                  href={downloadHref}
                  download={flyerFileName(paperSize, "pdf")}
                  className={outlineActionClass}
                  onClick={() => track("start_flyer_downloaded", { paperSize, method: "download" })}
                >
                  {t("start.done.flyer.download")}
                </a>
              )}
              {showChrome && (
                <div>
                  <a
                    href={chromeIntentUrl(publicFlyer)}
                    className={showShare ? outlineActionClass : tintActionClass}
                    onClick={() => track("start_flyer_downloaded", { paperSize, method: "chrome" })}
                  >
                    {t("start.done.flyer.openChrome")}
                  </a>
                  <p className="mt-2 text-center text-sm text-gray-500">{t("start.done.flyer.openChromeNote")}</p>
                </div>
              )}
              {showCopyFlyer && (
                <CopyAction
                  text={publicFlyer}
                  label={t("start.done.flyer.copyLink")}
                  copiedLabel={t("start.done.flyer.copied")}
                  manualLabel={t("start.done.flyer.manualLabel")}
                  manualHint={t("start.done.manualHint")}
                  tip={t("start.done.flyer.copyTip")}
                  onCopied={() => track("start_flyer_link_copied", { paperSize })}
                />
              )}
            </div>
          )}
        </div>
        <p className="text-sm text-gray-500">{t("start.done.flyer.dashboardNote")}</p>
      </Section>

      {formUrl && (
        <Section title={t("start.done.form.title")} description={t("start.done.form.description")}>
          <p className="break-all rounded-xl bg-gray-50 px-3.5 py-3 font-mono text-sm text-gray-900">{formUrl}</p>
          <CopyAction
            text={formUrl}
            label={t("start.done.form.copy")}
            copiedLabel={t("start.done.form.copied")}
            manualLabel={t("start.done.form.manualLabel")}
            manualHint={t("start.done.manualHint")}
            onCopied={() => track("start_form_link_copied")}
          />
          <div className="flex flex-col items-center gap-2 pt-1">
            <div role="img" aria-label={t("start.done.form.qrLabel")} className="rounded-xl bg-white p-2 ring-1 ring-gray-200">
              <QRCodeSVG value={formUrl} size={152} level="M" marginSize={1} />
            </div>
            <p className="text-center text-sm text-gray-500">{t("start.done.form.qrHint")}</p>
          </div>
        </Section>
      )}

      {(live || slow) && (
        <Section title={t("start.done.next.title")}>
          <ul className="space-y-2">
            <NextStep
              to="/team"
              title={t("start.done.next.staff.title")}
              body={t("start.done.next.staff.body")}
              onClick={(event) => goTo(event, "/team")}
            />
            <NextStep
              to="/notifications"
              title={t("start.done.next.push.title")}
              body={t("start.done.next.push.body")}
              onClick={(event) => goTo(event, "/notifications")}
            />
            {inApp && (
              <NextStep
                to="/start/try"
                title={t("start.done.next.wallet.title")}
                body={t("start.done.next.wallet.body")}
              />
            )}
          </ul>
        </Section>
      )}

      <StickyFooter>
        <PrimaryButton
          onClick={(event) => goTo(event, "/dashboard")}
          loading={!live && !slow}
          disabled={!live && !slow}
        >
          {t("start.done.dashboard")}
        </PrimaryButton>
      </StickyFooter>
    </div>
  );
}
